import React, { useEffect, useState, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@material-ui/core";
import { MuiThemeProvider, createMuiTheme } from "@material-ui/core/styles";

const themeButton = createMuiTheme({
  palette: {
    primary: {
      main: "#00abbd",
      contrastText: "#FFFFFF",
    },
  },
});

const MAX_VIOLATIONS = 3;

const FullscreenEnforcer = ({
  children,
  enableCount = false,
  onMaxViolation,
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [violationCount, setViolationCount] = useState(0);
  const violationCountRef = useRef(0);
  const violationFromFullscreenRef = useRef(false);

  const requestFullscreen = () => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    }
  };

  const checkFullscreen = () => {
    return !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
  };

  const handleKeyDown = (e) => {
    const key = e.key;
    const code = e.code;
    const keyCode = e.keyCode;

    // ESC
    if (key === "Escape" || keyCode === 27) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      return false;
    }

    // Tab
    if (key === "Tab" || keyCode === 9) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    // F1-F12
    if (e.key >= "F1" && e.key <= "F12") {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    // Print Screen
    if (key === "PrintScreen" || keyCode === 44 || code === "PrintScreen") {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      setTimeout(() => {
        try {
          navigator.clipboard.writeText("");
        } catch (_) {}
      }, 100);
      return false;
    }

    // Context Menu key
    if (key === "ContextMenu" || keyCode === 93) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    // Ctrl combinations
    if (e.ctrlKey) {
      if (
        [
          "a",
          "A",
          "f",
          "F",
          "r",
          "R",
          "t",
          "T",
          "n",
          "N",
          "w",
          "W",
          "i",
          "I",
          "u",
          "U",
          "p",
          "P",
          "c",
          "C",
          "v",
          "V",
          "x",
          "X",
        ].includes(key)
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      if (
        e.shiftKey &&
        ["i", "I", "j", "J", "s", "S", "u", "U"].includes(key)
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      if (key === "PrintScreen" || code === "PrintScreen" || keyCode === 44) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    }

    // Alt combinations
    if (e.altKey) {
      if (
        ["F4", "Tab", "PrintScreen"].includes(key) ||
        code === "PrintScreen" ||
        keyCode === 44
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    }

    // Windows key standalone
    if ((key === "Meta" || key === "Win") && !e.ctrlKey && !e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }

    // Win/Cmd combinations
    if (e.metaKey) {
      if (
        [
          "d",
          "D",
          "l",
          "L",
          "e",
          "E",
          "r",
          "R",
          "s",
          "S",
          "m",
          "M",
          "Tab",
          "PrintScreen",
          "q",
          "Q",
          "h",
          "H",
          "t",
          "T",
          "n",
          "N",
          "w",
          "W",
          " ",
        ].includes(key) ||
        code === "PrintScreen"
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      if (
        e.shiftKey &&
        ["3", "#", "4", "$", "5", "%", "6", "^"].includes(key)
      ) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      if (e.ctrlKey && e.shiftKey && ["3", "4", "5"].includes(key)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
      if (e.altKey && key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    }

    // Fn key
    if (code === "Fn" || key === "Fn") {
      e.preventDefault();
      return false;
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  const handleSelectStart = (e) => {
    if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
    e.preventDefault();
  };

  const handleCopy = (e) => {
    e.preventDefault();
    return false;
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = checkFullscreen();
      setIsFullscreen(isNowFullscreen);

      if (!isNowFullscreen) {
        violationFromFullscreenRef.current = true;
        setTimeout(() => {
          violationFromFullscreenRef.current = false;
        }, 500);

        if (enableCount) {
          violationCountRef.current += 1;
          const newCount = violationCountRef.current;
          setViolationCount(newCount);
          if (newCount >= MAX_VIOLATIONS) {
            onMaxViolation && onMaxViolation();
          } else {
            setShowWarning(true);
          }
        } else {
          setShowWarning(true);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        if (enableCount) {
          if (violationFromFullscreenRef.current) return;

          violationCountRef.current += 1;
          const newCount = violationCountRef.current;
          setViolationCount(newCount);
          if (newCount >= MAX_VIOLATIONS) {
            onMaxViolation && onMaxViolation();
          } else {
            setShowWarning(true);
          }
        } else {
          setShowWarning(true);
        }
      }
    };

    const handleBlur = () => {
      setShowWarning(true);
    };

    const monitorInterval = setInterval(() => {
      if (!checkFullscreen() || document.hidden) {
        setShowWarning(true);
      }
    }, 500);

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleBlur);
    document.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("selectstart", handleSelectStart);
    document.addEventListener("copy", handleCopy);

    const timer = setTimeout(() => {
      if (!checkFullscreen()) {
        setShowWarning(true);
      }
    }, 500);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener(
        "webkitfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "mozfullscreenchange",
        handleFullscreenChange,
      );
      document.removeEventListener(
        "MSFullscreenChange",
        handleFullscreenChange,
      );
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("selectstart", handleSelectStart);
      document.removeEventListener("copy", handleCopy);
      clearInterval(monitorInterval);
      clearTimeout(timer);
    };
  }, [enableCount, onMaxViolation]);

  const handleOkClick = () => {
    setShowWarning(false);
    requestFullscreen();
    setTimeout(() => {
      window.focus();
    }, 100);
  };

  const isLastWarning = violationCount >= MAX_VIOLATIONS - 1;
  const titleBgColor =
    enableCount && violationCount > 0 ? "#e53935" : "#00abbd";

  return (
    <>
      {children}

      <MuiThemeProvider theme={themeButton}>
        <Dialog
          open={showWarning}
          disableEscapeKeyDown
          disableBackdropClick
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: 15,
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            style={{ backgroundColor: titleBgColor, color: "white" }}
          >
            {enableCount && violationCount > 0
              ? `⚠️ Peringatan Fullscreen (${violationCount}/${MAX_VIOLATIONS})`
              : "Fullscreen Required"}
          </DialogTitle>

          <DialogContent style={{ marginTop: 20 }}>
            {enableCount && violationCount > 0 ? (
              <div>
                <p style={{ fontSize: 16, lineHeight: 1.6, margin: 0 }}>
                  Anda terdeteksi keluar dari mode <i>fullscreen</i>.
                </p>

                <div
                  style={{
                    margin: "16px 0",
                    padding: "12px 16px",
                    backgroundColor: isLastWarning ? "#fdecea" : "#fff8e1",
                    borderLeft: `4px solid ${isLastWarning ? "#e53935" : "#ffc107"}`,
                    borderRadius: 4,
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      fontWeight: "bold",
                      color: isLastWarning ? "#e53935" : "#f57c00",
                      fontSize: 15,
                    }}
                  >
                    Peringatan ke-{violationCount} dari {MAX_VIOLATIONS}
                  </p>
                  {isLastWarning ? (
                    <p
                      style={{
                        margin: "6px 0 0",
                        color: "#e53935",
                        fontSize: 14,
                      }}
                    >
                      ⛔ <b>Ini adalah peringatan terakhir!</b> Jika Anda keluar
                      dari fullscreen sekali lagi, jawaban akan{" "}
                      <b>otomatis dikumpulkan</b>.
                    </p>
                  ) : (
                    <p
                      style={{
                        margin: "6px 0 0",
                        color: "#795548",
                        fontSize: 14,
                      }}
                    >
                      Jika keluar fullscreen sampai <b>{MAX_VIOLATIONS} kali</b>
                      , jawaban akan otomatis dikumpulkan.
                    </p>
                  )}
                </div>

                <p style={{ fontSize: 15, lineHeight: 1.6, margin: 0 }}>
                  Silakan klik tombol{" "}
                  <strong style={{ color: "#00abbd" }}>OK</strong> untuk kembali
                  ke mode fullscreen.
                </p>
              </div>
            ) : (
              <p style={{ fontSize: 16, lineHeight: 1.6 }}>
                Untuk melanjutkan assessment, aplikasi harus dalam mode
                fullscreen.
                <br />
                Silakan klik tombol{" "}
                <strong style={{ color: "#00abbd" }}>OK</strong> di bawah untuk
                mengaktifkan fullscreen.
              </p>
            )}
          </DialogContent>

          <DialogActions style={{ padding: 20 }}>
            <Button
              onClick={handleOkClick}
              color="primary"
              variant="contained"
              size="large"
              fullWidth
              style={{
                borderRadius: 10,
                textTransform: "none",
                fontWeight: "bold",
                padding: "12px",
                backgroundColor:
                  isLastWarning && enableCount ? "#e53935" : undefined,
              }}
            >
              OK, Kembali ke Fullscreen
            </Button>
          </DialogActions>
        </Dialog>
      </MuiThemeProvider>
    </>
  );
};

export default FullscreenEnforcer;
